import { round, curry } from 'lodash';
import travelGraphData from './fixtures/travelGraphData.json';

const convertFeetToMeters = (length: number) => length * 0.3048;
const convertFahrenheitToCelsius = (temp: number) => (temp - 32) * (5 / 9);

export const convertTemperatureUnits = curry((units: string, tempInFahrenheit: number) => {
  if (!tempInFahrenheit) return tempInFahrenheit;
  if (units === 'C') return round(convertFahrenheitToCelsius(tempInFahrenheit));
  return round(tempInFahrenheit);
});

export const convertSurfHeightUnits = curry((units: string, surfHeightInFeet: number) => {
  if (!surfHeightInFeet) return surfHeightInFeet;
  if (units === 'M') return round(convertFeetToMeters(surfHeightInFeet), 1);
  return round(surfHeightInFeet, 1);
});

const monthMap = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const xScale = {
  type: 'linear',
  min: 0,
  max: 'auto',
};

interface AirTempData {
  avg: number;
  min: number;
  max: number;
}

const formatAirTemp = (data: AirTempData[], temperatureUnit: string) =>
  data.map((month: any, index: any) => {
    const min = convertTemperatureUnits(temperatureUnit, month.min);
    const max = convertTemperatureUnits(temperatureUnit, month.max);
    const avg = convertTemperatureUnits(temperatureUnit, month.avg);
    return {
      month: monthMap[index],
      min,
      max,
      avg,
      range: max - min,
    };
  });

interface ChanceOfScoringData {
  fun: number;
  funColor: string;
  junky: number;
  junkyColor: string;
  pumping: number;
  pumpingColor: string;
  rideable: number;
  rideableColor: string;
}

const formatChanceOfScoring = (data: ChanceOfScoringData[]) =>
  data.map((month: any, index: any) => ({
    month: monthMap[index],
    fun: month.fun,
    funColor: 'rgba(49, 78, 230, .7)',
    poor: month.junky,
    poorColor: 'rgba(228, 231, 237, 1)',
    pumping: month.pumping,
    pumpingColor: 'rgba(49, 78, 230, 1)',
    rideable: month.rideable,
    rideableColor: 'rgba(49, 78, 230, .3)',
  }));

interface WeatherData {
  precipitation: number;
  waterTemp: number;
}

const MM_IN_ONE_INCH = 25.4;

const formatRainfall = (data: WeatherData[]) => {
  const formattedData = data.map(({ precipitation }: WeatherData, index: number) => ({
    x: index,
    y: round(precipitation / MM_IN_ONE_INCH, 2),
  }));
  return {
    data: [...formattedData, { x: formattedData.length, y: formattedData[0].y }],
    id: 'rainfall',
    xScale,
  };
};

const formatWaterTemp = (data: WeatherData[], temperatureUnit: string) => {
  const formattedData = data.map(({ waterTemp }: WeatherData, index: number) => ({
    x: index,
    y: convertTemperatureUnits(temperatureUnit, waterTemp),
  }));
  return {
    data: [...formattedData, { x: formattedData.length, y: formattedData[0].y }],
    id: 'waterTemp',
    xScale,
  };
};

interface SurfData {
  avg: number;
  min: number;
  max: number;
}

const formatSurf = (data: SurfData[], surfHeightUnit: string) => {
  const minData = {
    id: 'Min.',
    color: 'rgba(49, 78, 230, .3)',
    data: [],
  };
  const avgData = {
    id: 'Avg.',
    color: 'rgba(49, 78, 230, .6)',
    data: [],
  };
  const maxData = {
    id: 'Max.',
    color: 'rgba(49, 78, 230, 1)',
    data: [],
  };
  data.forEach(({ min, avg, max }: SurfData, index: number) => {
    // @ts-ignore
    minData.data.push({ x: index, y: convertSurfHeightUnits(surfHeightUnit, min) });
    // @ts-ignore
    avgData.data.push({ x: index, y: convertSurfHeightUnits(surfHeightUnit, avg) });
    // @ts-ignore
    maxData.data.push({ x: index, y: convertSurfHeightUnits(surfHeightUnit, max) });
  });
  return [
    {
      ...minData,
      // @ts-ignore
      data: [...minData.data, { x: minData.data.length, y: minData.data[0].y }],
    },
    {
      ...avgData,
      // @ts-ignore
      data: [...avgData.data, { x: avgData.data.length, y: avgData.data[0].y }],
    },
    {
      ...maxData,
      // @ts-ignore
      data: [...maxData.data, { x: maxData.data.length, y: maxData.data[0].y }],
    },
  ];
};

interface WindData {
  bad: number;
  good: number;
}

const formatWind = (data: WindData[]) =>
  data.map((month: any, index: any) => ({
    percentage: month.good,
    month: monthMap[index],
  }));

interface Units {
  surfHeight: string;
  temperature: string;
}
const getTravelGraphData = (zoneId: string, units: Units) => {
  // @ts-ignore
  const zoneData = travelGraphData[zoneId];
  let airTemp;
  let chanceOfScoring;
  let rainfall;
  let surf;
  let waterTemp;
  let wind;
  if (zoneData) {
    airTemp = formatAirTemp(zoneData.data.airTemp, units.temperature);
    chanceOfScoring = formatChanceOfScoring(zoneData.data.chanceOfScoring);
    rainfall = formatRainfall(zoneData.data.weather);
    surf = formatSurf(zoneData.data.surf, units.surfHeight);
    waterTemp = formatWaterTemp(zoneData.data.weather, units.temperature);
    wind = formatWind(zoneData.data.wind);
  }
  return {
    airTemp,
    chanceOfScoring,
    wind,
    surf,
    rainfall,
    waterTemp,
  };
};

export default getTravelGraphData;
